import { createContext, ReactNode, RefObject, useContext } from 'react'

import { AbilityType } from 'app/frontend/types/custom/Abilities'
import { GameVersion } from 'app/frontend/types/custom/CharacterClass'
import RaidPlan, {
  ConfiguredCustomAbility,
  RaidPlanTimeline,
} from 'app/frontend/types/custom/RaidPlan'
import { TeamPlanComputedData } from '../hooks/useTeamPlanComputedData'
import { RaidPlanOwner } from 'app/frontend/base/api/raid_plans'

type Viewer = {
  key: string
  user_id: number | null
  label: string | null
}

type CursorState = {
  viewer: Viewer
  x: number
  y: number
  context: string
}

type TimelineZoom = {
  start: number
  end: number
}

type TeamPlanTimelineContextValue = {
  canEdit: boolean
  owner: RaidPlanOwner
  basePath: string
  planListPathPrefix: string
  rosterMemberships: wowaudit.TeamMembership[]
  raidPlan: RaidPlan
  timeline: RaidPlanTimeline
  isInitializing: boolean
  selectedType: AbilityType
  setSelectedType: (value: AbilityType) => void
  zoom: TimelineZoom | null
  setZoom: (zoom: TimelineZoom | null) => void
  setDragFrameMax: (value: number | null) => void
  computed: TeamPlanComputedData
  timelineRootRef: RefObject<HTMLDivElement>
  viewers: Viewer[]
  cursors: CursorState[]
  showRosterImport: boolean
  cursorContext: string
  setAssignmentDialogOpen: (open: boolean) => void
  sendCursor: (x: number | null, y: number | null, context: string) => void
  updateTimeline: (
    updater: (next: RaidPlanTimeline) => void,
    pushHistory?: boolean,
  ) => void
  applyPlanFromSource: (source: {
    timeline: RaidPlanTimeline
    message?: string
  }) => void
  showAssignmentDifficulty?: boolean
  showAssignmentDescription?: boolean
  gameVersion?: GameVersion
  configuredCustomAbilities?: ConfiguredCustomAbility[]
}

const TeamPlanTimelineContext = createContext<
  TeamPlanTimelineContextValue | undefined
>(undefined)

type Props = {
  value: TeamPlanTimelineContextValue
  children: ReactNode
}

const TeamPlanTimelineProvider = ({ value, children }: Props) => {
  return (
    <TeamPlanTimelineContext.Provider value={value}>
      {children}
    </TeamPlanTimelineContext.Provider>
  )
}

const useTeamPlanTimeline = () => {
  const context = useContext(TeamPlanTimelineContext)
  if (!context) throw new Error('TeamPlanTimelineContext missing')
  return context
}

export { TeamPlanTimelineProvider, useTeamPlanTimeline }
export type { CursorState, Viewer, TimelineZoom }
